import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import * as ReactIntl from 'react-intl';

export const {
  FormattedDate,
  FormattedTime,
  FormattedRelative,
  FormattedNumber,
  FormattedPlural,
  FormattedMessage,
  FormattedHTMLMessage,
} = ReactIntl;

export const TRANSLATED_CLASS = 'intl-text';
export const MISSING_CLASS = 'translation-missing';

// Use this within other react components when you need a rendered translated message
export class TranslatedMessage extends React.Component {
  static contextTypes = {
    intl: ReactIntl.intlShape,
  };

  static propTypes = {
    id: PropTypes.string,
    values: PropTypes.object,
  };

  get className() {
    return (message, messageId) => {
      const baseClassName = classNames(TRANSLATED_CLASS, this.props.className);
      return classNames(baseClassName, {
        [MISSING_CLASS]: message === messageId,
      });
    };
  }

  get messageId() {
    const { id, ...props } = this.props;
    return getMessageId(id, props, this.context.intl);
  }

  renderMessageContent() {
    const parts = [].slice.call(arguments);
    const className = this.className(parts[0], this.messageId);
    const { textComponent: Text } = this.context.intl;

    return React.createElement(Text, { className }, ...parts);
  }

  render() {
    return (
      <ReactIntl.FormattedMessage {...this.props} id={this.messageId}>
        {this.renderMessageContent.bind(this)}
      </ReactIntl.FormattedMessage>
    );
  }
}

// Use this when you need to get just the translated string. Must provide intl.
export const translateMessage = (intl, id, values) => {
  const messageId = getMessageId(id, { values }, intl);
  return intl.formatMessage({ id: messageId }, values);
};

// Curried version of translateMessage.
export const messageTranslator = intl => (id, values) =>
  translateMessage(intl, id, values);

/*
 * In rails, each plural form has its own key, and we need to determine it
 * from the numeric value of <count>. A potential issue may arise since the algorithm
 * used here is not the same as in Rails.
 *
 * See:
 * http://guides.rubyonrails.org/i18n.html#pluralization.
 * https://github.com/yahoo/react-intl/wiki/API#formatplural
 * http://www.unicode.org/cldr/charts/latest/supplemental/language_plural_rules.html
 */
const getMessageId = (id, props, intl) => {
  let messageId = id;
  if (props.values && props.values.count !== undefined) {
    const pluralForm = intl.formatPlural(props.values.count, props);
    messageId = `${id}.${pluralForm}`;
  }

  return getIdOrDefault(messageId, intl.messages);
};

/*
 * Used for testing only. Set global.defaultTranslations to false if you dont
 * wish for messages to have a default identifier during tests.
 */
const getIdOrDefault = (id, messages) => {
  const isTestEnvironment = process.env.NODE_ENV == 'test';
  const defaultsEnabled = global.defaultTranslationsEnabled;
  if (isTestEnvironment && defaultsEnabled && !messages[id]) {
    return 'default';
  }

  return id;
};

export const IntlProvider = ({ children }) => (
  <ReactIntl.IntlProvider
    locale={global.translations.locale}
    messages={global.translations.messages}
  >
    {children}
  </ReactIntl.IntlProvider>
);



// WEBPACK FOOTER //
// ./app/javascript/common/components/Intl.js